import { m } from '@pricedrop/i18n';
import { Link, useLocation, useNavigate } from '@tanstack/react-router';
import Bell from 'lucide-react/dist/esm/icons/bell';
import LogOut from 'lucide-react/dist/esm/icons/log-out';
import Star from 'lucide-react/dist/esm/icons/star';
import UserCircle2 from 'lucide-react/dist/esm/icons/user-circle-2';
import * as React from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { signOut, useSession } from '@/lib/auth-client';

function getInitials(name?: string | null, email?: string) {
  if (name) {
    return name
      .split(' ')
      .filter(Boolean)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? '')
      .join('');
  }

  return (email || 'U').charAt(0).toUpperCase();
}

export function UserMenu() {
  const navigate = useNavigate();
  const location = useLocation();
  const { data: session, isPending } = useSession();
  const [isSigningOut, setIsSigningOut] = React.useState(false);

  if (isPending) {
    return null;
  }

  if (!session?.user) {
    return (
      <Button asChild variant="outline" size="sm">
        <Link to="/login" search={{ redirect: location.pathname + (location.searchStr || '') }}>
          {m.auth_login_title()}
        </Link>
      </Button>
    );
  }

  const initials = getInitials(session.user.name, session.user.email);

  async function handleSignOut() {
    setIsSigningOut(true);
    await signOut();
    setIsSigningOut(false);
    await navigate({ to: '/' });
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon" className="ms-1 rounded-full">
          <Avatar className="size-8">
            <AvatarImage
              src={session.user.image || undefined}
              alt={session.user.name || session.user.email}
            />
            <AvatarFallback>{initials}</AvatarFallback>
          </Avatar>
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-52">
        <DropdownMenuLabel className="font-normal">
          <p className="font-medium text-sm">{session.user.name || session.user.email}</p>
        </DropdownMenuLabel>
        <DropdownMenuSeparator />
        <DropdownMenuItem asChild>
          <Link
            to="/account"
            search={{ tab: 'profile' }}
            className="flex w-full items-center gap-2"
          >
            <UserCircle2 className="size-4" />
            {m.account_tabs_profile()}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to="/account"
            search={{ tab: 'notifications' }}
            className="flex w-full items-center gap-2"
          >
            <Bell className="size-4" />
            {m.account_tabs_notifications()}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuItem asChild>
          <Link
            to="/account"
            search={{ tab: 'reviews' }}
            className="flex w-full items-center gap-2"
          >
            <Star className="size-4" />
            {m.account_tabs_reviews()}
          </Link>
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem
          variant="destructive"
          className="gap-2"
          disabled={isSigningOut}
          onSelect={handleSignOut}
        >
          <LogOut className="size-4" />
          {m.auth_logout()}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
